import React, { useState, useEffect, useRef } from 'react';
import './EditPrimeSchedule.css';
import { axiosInstance } from '../../../../utils/tokenUtils';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CalendarTodayIcon from '../../../../assets/ScheduleIcon/calendar.svg';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Chip, Autocomplete, TextField, CircularProgress, Alert, Button } from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import ReactDOM from 'react-dom';
import ScheduleCreationSuccessModel from '../../CreateSessionModel/ScheduleCreationSuccessModel/ScheduleCreationSuccessModel';
import RefreshIcon from '@mui/icons-material/Refresh';
import { toast } from 'react-hot-toast';

// Import time picker components
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';

// Setup dayjs plugins for timezone handling
dayjs.extend(utc);
dayjs.extend(timezone);

// Custom renderer for time picker
const customTimeViewRenderer = props => {
  const { view, value, onChange, ampm, ...other } = props;

  // For hours view, render the standard clock
  if (view === 'hours') {
    return renderTimeViewClock({
      view,
      value,
      onChange,
      ampm,
      ...other,
    });
  }

  // For minutes view, which is shown after hours are selected
  return renderTimeViewClock({
    view,
    value,
    onChange,
    ampm,
    ...other,
  });
};

const EditPrimeSchedule = ({ open, onClose, availabilityId }) => {
  // State variables for form fields
  const [subject, setSubject] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [level, setLevel] = useState('');
  const [selectedDates, setSelectedDates] = useState([]);
  const [fromTime, setFromTime] = useState('');
  const [toTime, setToTime] = useState('');
  const [description, setDescription] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedTags, setSelectedTags] = useState([]);
  const [inputTag, setInputTag] = useState('');

  // States for API integration
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [apiError, setApiError] = useState('');
  const [dataFetched, setDataFetched] = useState(false);
  const [availabilityData, setAvailabilityData] = useState(null);
  const [teacherData, setTeacherData] = useState(null);
  const [eventTypeId, setEventTypeId] = useState('');

  // States for time picker
  const [activeTimePicker, setActiveTimePicker] = useState(null);
  const [clockView, setClockView] = useState('hours');

  // Data for form population
  const [subjects, setSubjects] = useState([]);
  const [levels, setLevels] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [primeSessionTimeFrame, setPrimeSessionTimeFrame] = useState(null);
  const [week1Dates, setWeek1Dates] = useState([]);
  const [week2Dates, setWeek2Dates] = useState([]);

  // References
  const timePickerRefs = useRef({});
  const calendarButtonRef = useRef(null);
  const activeToastIdRef = useRef(null);

  const showErrorToast = message => {
    if (!message) return;
    if (activeToastIdRef.current) {
      toast.dismiss(activeToastIdRef.current);
    }
    activeToastIdRef.current = toast.error(message);
  };

  // Reset form on close
  useEffect(() => {
    if (!open) {
      resetForm();
    } else {
      setCurrentDate(new Date());
      setCalendarDate(new Date());
      setSelectedDates([]);
    }
  }, [open]);

  // Fetch data when modal opens
  useEffect(() => {
    if (open && availabilityId) {
      console.log('Fetching data for prime availability ID:', availabilityId);
      setIsLoading(true);
      fetchData();
    }
  }, [open, availabilityId]);

  // Update time slots based on fromTime
  useEffect(() => {
    if (fromTime) {
      // Parse the fromTime using dayjs
      const fromTimeObj = stringToTime(fromTime);
      if (fromTimeObj) {
        // Add 2 hours to the fromTime
        const toTimeObj = fromTimeObj.add(2, 'hours');
        // Format and set the toTime
        setToTime(toTimeObj.format('hh:mm A'));
      }
    } else {
      // If fromTime is cleared, also clear toTime
      setToTime('');
    }
  }, [fromTime]);

  // Update available tags when subject changes
  useEffect(() => {
    if (subject && teacherData) {
      const selectedSubject = teacherData.subjects.find(s => s.title === subject);

      if (selectedSubject) {
        setAvailableTags(selectedSubject.tags);
        setSubjectId(selectedSubject.id);
      } else {
        setAvailableTags([]);
        setSubjectId('');
      }
    }
  }, [subject, teacherData]);

  // Fetch both teacher and availability data
  const fetchData = async () => {
    try {
      setApiError('');
      setIsLoading(true);

      // Fetch both data in parallel
      const [teacherResponse, availabilityResponse] = await Promise.all([
        axiosInstance.get('/teachers'),
        axiosInstance.get(`/teachers/availability/${availabilityId}`),
      ]);

      console.log('Teacher data received:', teacherResponse.data);
      console.log('Prime availability data received:', availabilityResponse.data);

      // Process teacher data
      const teacherProfileData = teacherResponse.data?.data || teacherResponse.data;
      setTeacherData(teacherProfileData);

      // Set subjects
      const subjectOptions = teacherProfileData.subjects.map(sub => ({
        value: sub.title,
        label: sub.title,
        id: sub.id,
        tags: sub.tags,
      }));
      setSubjects(subjectOptions);

      // Extract levels of teaching
      const levelOptions = teacherProfileData.level_of_teaching.map(level => {
        // Convert snake_case to Title Case
        const formattedLevel = level
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        return {
          value: level, // Keep original snake_case for API
          label: formattedLevel, // Display formatted version
        };
      });
      setLevels(levelOptions);

      // Set prime session time frame
      setPrimeSessionTimeFrame(teacherProfileData.prime_session_time_frame);

      // Process availability data
      const availData = availabilityResponse.data?.data || availabilityResponse.data;

      if (availData) {
        setAvailabilityData(availData);
        processAvailabilityData(availData, teacherProfileData, levelOptions);
      } else {
        console.warn('No data found in the availability response');
        // Still allow editing with empty data
        setDataFetched(true);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      // Don't block the UI with error, just log it
      console.warn(`Failed to load data: ${error.message || 'Unknown error'}`);
      setApiError(`Failed to load data: ${error.message || 'Unknown error'}`);
      showErrorToast(`Failed to load data: ${error.message || 'Unknown error'}`);
      setDataFetched(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Process availability data to populate the form
  const processAvailabilityData = (data, teacherData, levelOptions) => {
    try {
      // Set event type ID for the PATCH request
      setEventTypeId(data.event_type_id || data.id);

      // Set basic fields
      setDescription(data.description || '');

      // Find and set subject by ID or by title match
      if (data.subject_id || data.subject) {
        const subjectId = data.subject_id;
        let foundSubject;

        if (subjectId) {
          // Find by ID
          foundSubject = teacherData.subjects.find(s => s.id === subjectId);
        } else if (data.subject) {
          // Find by title match (case insensitive)
          foundSubject = teacherData.subjects.find(
            s => s.title.toLowerCase() === data.subject.toLowerCase()
          );
        }

        if (foundSubject) {
          setSubject(foundSubject.title);
          setSubjectId(foundSubject.id);
          setAvailableTags(foundSubject.tags || []);

          // Set tags if available
          if (data.tags && Array.isArray(data.tags)) {
            if (typeof data.tags[0] === 'string') {
              // If tags are strings, match with available tags by title
              const matchedTags = data.tags
                .map(tagTitle =>
                  foundSubject.tags?.find(t => t.title.toLowerCase() === tagTitle.toLowerCase())
                )
                .filter(Boolean);
              setSelectedTags(matchedTags);
            } else if (
              typeof data.tags[0] === 'number' ||
              (typeof data.tags[0] === 'string' && data.tags[0].match(/^[0-9a-f-]+$/))
            ) {
              // If tags are IDs (numeric or UUID strings)
              const matchedTags = data.tags
                .map(tagId => foundSubject.tags?.find(t => t.id === tagId))
                .filter(Boolean);
              setSelectedTags(matchedTags);
            } else if (typeof data.tags[0] === 'object') {
              // If tags are already objects
              setSelectedTags(data.tags);
            }
          }
        }
      }

      // Set level
      if (data.level_of_teaching) {
        const levelValue = data.level_of_teaching;
        const foundLevel = levelOptions.find(l => l.value === levelValue);
        if (foundLevel) {
          setLevel(foundLevel.label);
        } else {
          // Fallback to formatted version of the level
          const formattedLevel = levelValue
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
          setLevel(formattedLevel);
        }
      }

      // Process time slots from different possible sources
      let timeProcessed = false;

      // Try to get time from overrides first
      if (data.overrides && data.overrides.length > 0) {
        const firstOverride = data.overrides[0];
        if (firstOverride.startTime) {
          setFromTime(
            convertUTCToLocal(firstOverride.startTime) || formatTo12Hour(firstOverride.startTime)
          );
          timeProcessed = true;
        }
        if (firstOverride.endTime) {
          setToTime(
            convertUTCToLocal(firstOverride.endTime) || formatTo12Hour(firstOverride.endTime)
          );
          timeProcessed = true;
        }
      }

      // If no time from overrides, try time_slots
      if (!timeProcessed && data.time_slots && data.time_slots.length > 0) {
        const slot = data.time_slots[0];
        if (slot.start_time) {
          setFromTime(convertUTCToLocal(slot.start_time) || formatTo12Hour(slot.start_time));
        }
        if (slot.end_time) {
          setToTime(convertUTCToLocal(slot.end_time) || formatTo12Hour(slot.end_time));
        }
      }

      // Process dates
      processDateData(data);

      setDataFetched(true);
    } catch (err) {
      console.error('Error processing availability data:', err);
      setApiError('Error processing session data. Please try again.');
      setDataFetched(true);
    }
  };

  // Helper to process date data from availability
  const processDateData = data => {
    try {
      const dates = [];
      const week1 = [];
      const week2 = [];

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // First, try to get dates from overrides (new format)
      if (data.overrides && Array.isArray(data.overrides) && data.overrides.length > 0) {
        console.log('Processing dates from overrides:', data.overrides);

        // Sort overrides by date
        const sortedOverrides = [...data.overrides].sort(
          (a, b) => new Date(a.date) - new Date(b.date)
        );

        // Get the first date to determine week boundaries
        const firstDate = new Date(sortedOverrides[0].date);
        const weekStart = new Date(firstDate);
        weekStart.setDate(firstDate.getDate() - firstDate.getDay()); // Go to beginning of week

        // Week boundary is 7 days after start
        const weekBoundary = new Date(weekStart);
        weekBoundary.setDate(weekStart.getDate() + 7);

        sortedOverrides.forEach(override => {
          const overrideDate = new Date(override.date);
          const normalized = new Date(overrideDate);
          normalized.setHours(0, 0, 0, 0);

          // Skip past dates
          if (normalized < today) return;

          // Push to the dates array
          dates.push(overrideDate);

          // Determine if it's week 1 or week 2
          if (overrideDate < weekBoundary) {
            week1.push(overrideDate);
          } else {
            week2.push(overrideDate);
          }
        });

        // Update state with the processed dates
        setSelectedDates(dates);
        setWeek1Dates(week1);
        setWeek2Dates(week2);

        // Set current date to the beginning of the first week
        if (week1.length > 0) {
          // Go to Sunday (beginning of week) of the first date
          const firstWeekDate = new Date(week1[0]);
          const dayOfWeek = firstWeekDate.getDay();
          firstWeekDate.setDate(firstWeekDate.getDate() - dayOfWeek);
          setCurrentDate(firstWeekDate);
          setCalendarDate(new Date(firstWeekDate)); // Also update calendar view
        }

        return; // Exit early as we've processed dates from overrides
      }

      // Fallback to week1_dates and week2_dates (old format)
      if (data.week1_dates && data.week1_dates.selected_dates) {
        const week1DateObjs = data.week1_dates.selected_dates
          .map(dateStr => new Date(dateStr))
          .filter(d => {
            const nd = new Date(d);
            nd.setHours(0, 0, 0, 0);
            return nd >= today;
          });
        week1.push(...week1DateObjs);
        dates.push(...week1DateObjs);
      }

      if (data.week2_dates && data.week2_dates.selected_dates) {
        const week2DateObjs = data.week2_dates.selected_dates
          .map(dateStr => new Date(dateStr))
          .filter(d => {
            const nd = new Date(d);
            nd.setHours(0, 0, 0, 0);
            return nd >= today;
          });
        week2.push(...week2DateObjs);
        dates.push(...week2DateObjs);
      }

      // If we have dates from week1/week2, update state
      if (dates.length > 0) {
        setSelectedDates(dates);
        setWeek1Dates(week1);
        setWeek2Dates(week2);

        // Set current date to the first date minus its day of week
        if (week1.length > 0) {
          const firstDate = new Date(week1[0]);
          const dayOfWeek = firstDate.getDay();
          firstDate.setDate(firstDate.getDate() - dayOfWeek);
          setCurrentDate(firstDate);
        }
      }
    } catch (err) {
      console.error('Error processing date data:', err);
      showErrorToast('Error processing session dates. Please try again.');
    }
  };

  // Utility function to convert string time to dayjs object
  const stringToTime = timeString => {
    if (!timeString) return null;
    // Parse time including AM/PM format
    return dayjs(`2023-01-01 ${timeString}`, 'YYYY-MM-DD hh:mm A');
  };

  // Function to convert UTC time to local time (12-hour format)
  const convertUTCToLocal = timeString => {
    if (!timeString) return null;
    // Assuming timeString is in 'HH:mm' format
    const [hours, minutes] = timeString.split(':');
    const utcDate = dayjs.utc().hour(hours).minute(minutes);
    return utcDate.local().format('hh:mm A');
  };

  // Function to convert local time to UTC
  const convertLocalToUTC = timeString => {
    if (!timeString) return null;
    // Assuming timeString is in 'HH:mm' format or '12-hour format with AM/PM'
    let localDate;
    if (timeString.includes('AM') || timeString.includes('PM')) {
      localDate = dayjs(`2023-01-01 ${timeString}`, 'YYYY-MM-DD hh:mm A');
    } else {
      const [hours, minutes] = timeString.split(':');
      localDate = dayjs().hour(hours).minute(minutes);
    }
    return localDate.utc().format('HH:mm');
  };

  // Format 24-hour time string to 12-hour format with AM/PM
  const formatTo12Hour = time24 => {
    if (!time24) return '';
    try {
      const [hours, minutes] = time24.split(':').map(Number);
      const period = hours >= 12 ? 'PM' : 'AM';
      const hours12 = hours % 12 || 12;
      return `${hours12.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${period}`;
    } catch (e) {
      console.error('Error formatting time:', e);
      return time24;
    }
  };

  // Reset form state
  const resetForm = () => {
    setSubject('');
    setSubjectId('');
    setLevel('');
    setSelectedDates([]);
    setFromTime('');
    setToTime('');
    setDescription('');
    setShowCalendar(false);
    setShowConfirmation(false);
    setCurrentDate(new Date());
    setCalendarDate(new Date());
    setSelectedTags([]);
    setInputTag('');
    setError('');
    setApiError('');
    setAvailabilityData(null);
    setDataFetched(false);
    setWeek1Dates([]);
    setWeek2Dates([]);
  };

  // Function to handle opening a time picker
  const handleTimePickerOpen = field => {
    // Only allow opening the "From" time picker
    if (field !== 'from') return;

    // Reset the current field's value when opening the picker to allow re-selection
    if (fromTime) {
      setActiveTimePicker({
        field,
        previousValue: fromTime, // Store previous value
      });
    } else {
      setActiveTimePicker({ field });
    }
    setClockView('hours'); // Always start with hours view
  };

  // Function to handle closing a time picker
  const handleTimePickerClose = () => {
    // If user cancels without selecting a new value, restore the previous value
    if (activeTimePicker?.previousValue) {
      const { field, previousValue } = activeTimePicker;

      // Only restore if the value was actually cleared (user didn't select a new time)
      if (field === 'from' && !fromTime) {
        setFromTime(previousValue);
      }
    }

    setActiveTimePicker(null);
    setClockView('hours'); // Reset to hours view
  };

  // Modified handleTimeChange to handle sequential time selection
  const handleTimeChange = (field, value) => {
    // Only allow changes to the 'from' field - 'to' time is calculated automatically
    if (field !== 'from') return;

    // If value is null, the field is being cleared
    if (!value) {
      setFromTime('');
      // toTime will be cleared by the useEffect
      setClockView('hours'); // Reset to hours view
      return;
    }

    // For hours selection, update the hours and keep existing minutes or use 00
    if (clockView === 'hours') {
      // If we already have a time value, extract the existing minutes
      if (fromTime) {
        const existingTime = stringToTime(fromTime);
        if (existingTime) {
          // Keep the existing minutes but update the hours
          const updatedTime = value.minute(existingTime.minute());
          const formattedTime = updatedTime.format('hh:mm A');
          setFromTime(formattedTime);
        } else {
          // If parsing failed, use 00 minutes with the new hour
          const formattedTime = value.minute(0).format('hh:mm A');
          setFromTime(formattedTime);
        }
      } else {
        // If no existing time, use 00 minutes with the new hour
        const formattedTime = value.minute(0).format('hh:mm A');
        setFromTime(formattedTime);
      }

      // Switch to minutes view to let user select minutes if desired
      setClockView('minutes');
      return;
    }

    // For minutes selection, format and update the complete time
    const formattedTime = value.format('hh:mm A');
    setFromTime(formattedTime);

    // Reset to hours view for next selection
    setClockView('hours');
  };

  // Check if date is within allowed time frame
  const isDateInTimeFrame = date => {
    // Normalize dates for comparison
    const candidate = new Date(date);
    candidate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let withinTimeFrame = true;
    if (primeSessionTimeFrame) {
      const startDate = new Date(primeSessionTimeFrame.start_date);
      const endDate = new Date(primeSessionTimeFrame.end_date);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      withinTimeFrame = candidate >= startDate && candidate <= endDate;
    }

    // Also ensure the date is not in the past
    return withinTimeFrame && candidate >= today;
  };

  // Handle date selection
  const handleDateClick = date => {
    // Check if date is within the allowed time frame
    if (!isDateInTimeFrame(date)) {
      setError('Date is outside the allowed prime session time frame');
      showErrorToast('Selected date is not allowed (past or out of time frame)');
      return;
    }

    const dateString = date.toDateString();

    // If already selected, remove it
    if (selectedDates.some(d => new Date(d).toDateString() === dateString)) {
      setSelectedDates(selectedDates.filter(d => new Date(d).toDateString() !== dateString));
    } else {
      // Check which week this date belongs to
      const weekNumber = Math.floor((date - currentDate) / (7 * 24 * 60 * 60 * 1000)) + 1;

      // Count how many dates are already selected for this week
      const week1Dates = selectedDates.filter(d => {
        const wkNum = Math.floor((new Date(d) - currentDate) / (7 * 24 * 60 * 60 * 1000)) + 1;
        return wkNum === 1;
      });

      const week2Dates = selectedDates.filter(d => {
        const wkNum = Math.floor((new Date(d) - currentDate) / (7 * 24 * 60 * 60 * 1000)) + 1;
        return wkNum === 2;
      });

      // Check if adding this date would exceed the 3-date limit for the week
      if (
        (weekNumber === 1 && week1Dates.length >= 3) ||
        (weekNumber === 2 && week2Dates.length >= 3)
      ) {
        setError(`You can select at most 3 dates for Week ${weekNumber}`);
        showErrorToast(`You can select at most 3 dates for Week ${weekNumber}`);
        return;
      }

      setSelectedDates([...selectedDates, date]);
    }
  };

  // Calendar date selection
  const handleCalendarDateSelect = date => {
    setCurrentDate(date);
    setShowCalendar(false);
  };

  // Week navigation
  const handlePrevWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const handleNextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  // Get day name abbreviation
  const getDayName = dayNum => {
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return dayNames[dayNum];
  };

  // Generate days for a week
  const generateWeekDays = (startDate, weekNumber) => {
    const days = [];
    const currentDay = new Date(startDate);

    // Start from the current selected date for week 1
    // For week 2, add 7 days to the current date
    if (weekNumber === 2) {
      currentDay.setDate(currentDay.getDate() + 7);
    }

    // Generate 7 days starting from the current date
    for (let i = 0; i < 7; i++) {
      const date = new Date(currentDay);
      days.push({
        date,
        day: date.getDate(),
        dayName: getDayName(date.getDay()),
        isInTimeFrame: isDateInTimeFrame(date),
      });
      currentDay.setDate(currentDay.getDate() + 1);
    }

    return days;
  };

  // Tag handling
  const handleTagChange = (event, newTags) => {
    setSelectedTags(newTags);
  };

  const handleTagInputChange = (event, newInputValue) => {
    setInputTag(newInputValue);
  };

  const handleTagDelete = tagToDelete => {
    setSelectedTags(selectedTags.filter(tag => tag.id !== tagToDelete.id));
  };

  // Format date for API
  const formatDateForAPI = date => {
    const d = new Date(date);
    // Format to local date first
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');

    // Create UTC datetime string
    return `${year}-${month}-${day}T00:00:00Z`;
  };

  // Validate selected dates distribution
  const validateSelectedDates = () => {
    // Count dates in each week
    const week1Dates = selectedDates.filter(date => {
      const weekNumber = Math.floor((date - currentDate) / (7 * 24 * 60 * 60 * 1000)) + 1;
      return weekNumber === 1;
    });

    const week2Dates = selectedDates.filter(date => {
      const weekNumber = Math.floor((date - currentDate) / (7 * 24 * 60 * 60 * 1000)) + 1;
      return weekNumber === 2;
    });

    if (week1Dates.length !== 3) {
      return 'You must select exactly 3 dates for week 1';
    }

    if (week2Dates.length !== 3) {
      return 'You must select exactly 3 dates for week 2';
    }

    return null;
  };

  // Get level value for API
  const getLevelApiValue = displayLevel => {
    // Find the level object with matching label
    const levelObj = levels.find(l => l.label === displayLevel);
    return levelObj ? levelObj.value : displayLevel.toLowerCase().replace(/\s+/g, '_');
  };

  // Validate form before submission
  const validateForm = () => {
    if (!subject) return 'Please select a subject';
    if (!level) return 'Please select a level to teach';
    if (selectedDates.length === 0) return 'Please select at least one date';
    if (!fromTime) return 'Please select a start time';
    if (!toTime) return 'Please select an end time';
    if (!description) return 'Please provide a short description';

    return null;
  };

  // Handle save action
  const handleSave = async () => {
    setError('');
    setApiError('');

    // Validate form
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      showErrorToast(validationError);
      return;
    }

    // Validate selected dates distribution
    const datesValidationError = validateSelectedDates();
    if (datesValidationError) {
      setError(datesValidationError);
      showErrorToast(datesValidationError);
      return;
    }

    try {
      setIsLoading(true);

      // Get the level value for API
      const levelApiValue = getLevelApiValue(level);

      // Separate selected dates into week1 and week2
      const week1DatesPayload = [];
      const week2DatesPayload = [];

      selectedDates.forEach(date => {
        const dateString = formatDateForAPI(date);
        const weekNumber = Math.floor((date - currentDate) / (7 * 24 * 60 * 60 * 1000)) + 1;
        if (weekNumber <= 1) {
          week1DatesPayload.push(dateString);
        } else {
          week2DatesPayload.push(dateString);
        }
      });

      // Parse the local times
      const fromTimeObj = stringToTime(fromTime);
      const toTimeObj = stringToTime(toTime);

      // Determine overnight
      const isOvernightSession = toTimeObj && fromTimeObj && toTimeObj.isBefore(fromTimeObj);

      // Convert to UTC HH:mm
      const startTimeFormatted = fromTimeObj ? convertLocalToUTC(fromTime) : null;
      let endTimeFormatted;
      if (isOvernightSession) {
        const toTimeNextDay = toTimeObj.add(1, 'day');
        endTimeFormatted = toTimeNextDay.utc().format('HH:mm');
      } else {
        endTimeFormatted = toTimeObj ? convertLocalToUTC(toTime) : null;
      }

      // Build time slots list
      const timeSlots = [
        {
          start_time: startTimeFormatted,
          end_time: endTimeFormatted,
        },
      ];

      // Prepare request body for edit endpoint
      const requestBody = {
        event_type_id: eventTypeId,
        subject: subjectId,
        levels_to_teach: levelApiValue,
        description: description,
        week1_dates: {
          selected_dates: week1DatesPayload,
        },
        week2_dates: {
          selected_dates: week2DatesPayload,
        },
        time_slots: timeSlots,
        tags: selectedTags.map(tag => tag.id || tag),
      };

      console.log('Sending request to update prime session:', requestBody);

      // Use the update endpoint
      const response = await axiosInstance.patch('/teachers/availability/prime', requestBody);
      console.log('Update response:', response.data);

      if (window.refreshCalendarData) {
        window.refreshCalendarData();
      }

      // Close the modal
      onClose();

      // Show success toast
      toast.success('Prime session updated successfully');
    } catch (error) {
      setIsLoading(false);
      console.error('Failed to update prime session:', error);

      // Format detailed error message
      let errorMessage = 'Failed to update prime session. Please try again.';
      const resp = error?.response;
      const data = resp?.data;
      if (data?.detail) {
        const det = data.detail;
        if (Array.isArray(det)) {
          errorMessage = det.map(d => d?.msg || d?.message || JSON.stringify(d)).join('\n');
        } else if (typeof det === 'string') {
          errorMessage = det;
        } else if (typeof det === 'object') {
          errorMessage = det.message || JSON.stringify(det);
        }
      } else if (data?.message) {
        errorMessage = data.message;
      } else if (resp?.status === 404) {
        errorMessage = 'The requested resource was not found. Please try again later.';
      } else if (resp?.status === 403) {
        errorMessage = 'You do not have permission to update this session.';
      } else if (resp?.status >= 500) {
        errorMessage = 'A server error occurred. Please try again later.';
      } else if (error.request) {
        errorMessage = 'No response received from server. Please check your internet connection.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setApiError(errorMessage);
      showErrorToast(errorMessage);
    }
  };

  // Render calendar month for date picker
  const renderCalendarMonth = () => {
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();

    const firstDayOfWeek = firstDay.getDay();

    const prevMonthLastDay = new Date(year, month, 0).getDate();

    const days = [];

    for (let i = 0; i < firstDayOfWeek; i++) {
      const day = prevMonthLastDay - firstDayOfWeek + i + 1;
      const date = new Date(year, month - 1, day);
      days.push({
        day,
        month: month - 1,
        year,
        isCurrentMonth: false,
        date: date,
        isInTimeFrame: isDateInTimeFrame(date),
      });
    }

    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      days.push({
        day: i,
        month,
        year,
        isCurrentMonth: true,
        date: date,
        isInTimeFrame: isDateInTimeFrame(date),
      });
    }

    const remainingDays = 7 - (days.length % 7 || 7);
    for (let i = 1; i <= remainingDays; i++) {
      const date = new Date(year, month + 1, i);
      days.push({
        day: i,
        month: month + 1,
        year,
        isCurrentMonth: false,
        date: date,
        isInTimeFrame: isDateInTimeFrame(date),
      });
    }

    const weeks = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }

    // Calculate the position for the calendar popup
    const calculatePosition = () => {
      if (!calendarButtonRef.current) return {};

      const buttonRect = calendarButtonRef.current.getBoundingClientRect();
      const topPosition = buttonRect.bottom + 5; // 5px offset
      const rightPosition = window.innerWidth - buttonRect.right;

      return {
        top: `${topPosition}px`,
        right: `${rightPosition}px`,
      };
    };

    const calendarStyle = calculatePosition();

    // Return the calendar to be rendered in a portal
    return ReactDOM.createPortal(
      <div className="calendar-popup" style={calendarStyle}>
        <div className="calendar-header">
          <button onClick={() => setCalendarDate(new Date(year, month - 1, 1))}>&lt;</button>
          <div className="calendar-title">
            <div>{new Intl.DateTimeFormat('en-US', { month: 'long' }).format(calendarDate)}</div>
            <div className="calendar-year">{year}</div>
          </div>
          <button onClick={() => setCalendarDate(new Date(year, month + 1, 1))}>&gt;</button>
        </div>
        <div className="calendar-days-header">
          <div>S</div>
          <div>M</div>
          <div>T</div>
          <div>W</div>
          <div>T</div>
          <div>F</div>
          <div>S</div>
        </div>
        <div className="calendar-grid">
          {weeks.map((week, weekIndex) => (
            <div key={weekIndex} className="calendar-week">
              {week.map((day, dayIndex) => {
                const isSelected = day.date.toDateString() === currentDate.toDateString();

                return (
                  <div
                    key={dayIndex}
                    className={`calendar-day ${!day.isCurrentMonth ? 'other-month' : ''} ${isSelected ? 'selected' : ''} ${!day.isInTimeFrame ? 'disabled' : ''}`}
                    onClick={() => day.isInTimeFrame && handleCalendarDateSelect(day.date)}
                  >
                    {day.day}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>,
      document.body
    );
  };

  // Render time picker as a portal
  const renderTimePickerPortal = () => {
    if (!activeTimePicker) return null;

    const { field } = activeTimePicker;
    const timeValue = field === 'from' ? stringToTime(fromTime) : stringToTime(toTime);

    // Get the reference for this time picker
    const inputElement = timePickerRefs.current[field];

    return ReactDOM.createPortal(
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <TimePicker
          value={timeValue}
          onChange={newValue => handleTimeChange(field, newValue)}
          onClose={handleTimePickerClose}
          className="time-picker-portal"
          ampm={true}
          format="hh:mm A"
          minutesStep={15}
          open={true}
          viewRenderers={{
            hours: customTimeViewRenderer,
            minutes: customTimeViewRenderer,
          }}
          views={['hours', 'minutes']}
          view={clockView}
          onViewChange={newView => setClockView(newView)}
          slots={{
            textField: () => null, // Hide the default text field
          }}
          slotProps={{
            popper: {
              style: { zIndex: 2500 },
              placement: 'bottom-start',
              anchorEl: inputElement,
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 5],
                  },
                },
              ],
            },
          }}
        />
      </LocalizationProvider>,
      document.body
    );
  };

  // Render loading state with retry option
  const renderLoading = () => (
    <div className="loading-container">
      <CircularProgress />
      <div className="loading-text">Loading availability data...</div>
    </div>
  );

  return (
    <div className="modal-overlay" style={{ display: open ? 'flex' : 'none' }}>
      {/* Main modal content */}
      {!showConfirmation && (
        <div className="modal">
          <div className="modal-header">
            <h2>Edit Prime Session</h2>
            <button className="close-button" onClick={onClose}>
              <CloseIcon />
            </button>
          </div>

          <div className="modal-content">
            {isLoading && !dataFetched ? (
              renderLoading()
            ) : (
              <>
                <div className="form-group">
                  <label>Subject</label>
                  <div className="select-wrapper">
                    <select
                      value={subject}
                      onChange={e => setSubject(e.target.value)}
                      className="form-control"
                      disabled={isLoading}
                    >
                      <option value="" disabled>
                        Select
                      </option>
                      {teacherData?.subjects.map((sub, index) => (
                        <option key={index} value={sub.title}>
                          {sub.title}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Tags</label>
                  <Autocomplete
                    multiple
                    id="tags-filled"
                    options={availableTags || []}
                    getOptionLabel={option => option.title || ''}
                    value={selectedTags}
                    onChange={handleTagChange}
                    inputValue={inputTag}
                    onInputChange={handleTagInputChange}
                    renderTags={() => null}
                    renderInput={params => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder={subject ? 'Type here...' : 'Select a subject first'}
                        className="tags-input"
                      />
                    )}
                    disabled={!subject || isLoading}
                    className="tags-autocomplete"
                    filterSelectedOptions
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                  />
                  {selectedTags.length > 0 && (
                    <div className="tags-container">
                      {selectedTags.map(tag => (
                        <Chip
                          key={tag.id}
                          label={tag.title}
                          onDelete={() => handleTagDelete(tag)}
                          className="tag-chip"
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      ))}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>Level to Teach</label>
                  <div className="select-wrapper">
                    <select
                      value={level}
                      onChange={e => setLevel(e.target.value)}
                      className="form-control"
                      disabled={isLoading}
                    >
                      <option value="" disabled>
                        Select
                      </option>
                      {levels.map((lvl, index) => (
                        <option key={index} value={lvl.label}>
                          {lvl.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group calendar-section">
                  <label>Choose your Dates</label>

                  <div className="calendar-navigation">
                    <div className="week-nav">
                      <button className="nav-arrow" onClick={handlePrevWeek}>
                        <KeyboardArrowLeftIcon />
                      </button>
                      <button className="nav-arrow" onClick={handleNextWeek}>
                        <KeyboardArrowRightIcon />
                      </button>
                    </div>
                    <button
                      className="calendar-picker-btn"
                      onClick={() => setShowCalendar(!showCalendar)}
                      ref={calendarButtonRef}
                    >
                      <img src={CalendarTodayIcon} alt="calendar" />
                    </button>
                    {showCalendar && renderCalendarMonth()}
                  </div>

                  <div className="calendar-container">
                    <div className="week-container">
                      <div className="week-label">Week 01 (3 days required)</div>
                      <div className="days-container">
                        {generateWeekDays(currentDate, 1).map((day, index) => {
                          const isSelected = selectedDates.some(
                            d => new Date(d).toDateString() === day.date.toDateString()
                          );
                          return (
                            <div
                              key={index}
                              className={`day-item ${isSelected ? 'selected' : ''} ${!day.isInTimeFrame ? 'disabled' : ''}`}
                              onClick={() => day.isInTimeFrame && handleDateClick(day.date)}
                            >
                              <div className="day-number-prime">{day.day}</div>
                              <div className="day-name">{day.dayName}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="week-container">
                      <div className="week-label">Week 02 (3 days required)</div>
                      <div className="days-container">
                        {generateWeekDays(currentDate, 2).map((day, index) => {
                          const isSelected = selectedDates.some(
                            d => new Date(d).toDateString() === day.date.toDateString()
                          );
                          return (
                            <div
                              key={index}
                              className={`day-item ${isSelected ? 'selected' : ''} ${!day.isInTimeFrame ? 'disabled' : ''}`}
                              onClick={() => day.isInTimeFrame && handleDateClick(day.date)}
                            >
                              <div className="day-number-prime">{day.day}</div>
                              <div className="day-name">{day.dayName}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label>Select Your Timings</label>
                  <div className="time-section">
                    <div className="time-inputs">
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <div className="time-input-group">
                          <span className="time-label">From</span>
                          <div
                            className="time-input-wrapper"
                            onClick={() => handleTimePickerOpen('from')}
                            ref={el => {
                              // Store the ref for this time input field
                              timePickerRefs.current['from'] = el;
                            }}
                          >
                            <div className="time-input-icon">
                              <AccessTimeIcon fontSize="small" />
                            </div>
                            <input
                              className="time-input-field"
                              value={fromTime || ''}
                              placeholder="Select time"
                              readOnly
                            />
                          </div>
                        </div>
                        <div className="time-input-group">
                          <span className="time-label">To</span>
                          <div
                            className="time-input-wrapper disabled"
                            ref={el => {
                              // Store the ref for this time input field
                              timePickerRefs.current['to'] = el;
                            }}
                          >
                            <div className="time-input-icon">
                              <AccessTimeIcon fontSize="small" />
                            </div>
                            <input
                              className="time-input-field"
                              value={toTime || ''}
                              placeholder="Auto-set (2 hours after start)"
                              readOnly
                              disabled={true} // To is disabled because it's calculated from fromTime
                            />
                          </div>
                        </div>
                      </LocalizationProvider>
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label>Write a short description</label>
                  <textarea
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    className="form-control description-input"
                    disabled={isLoading}
                    rows={4}
                  />
                </div>
              </>
            )}
          </div>

          <div className="modal-footer">
            <button className="cancel-button-prime" onClick={onClose}>
              Cancel
            </button>
            <button
              className="save-button-prime"
              onClick={handleSave}
              disabled={isLoading || !dataFetched}
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>

          {/* Add the time picker portal */}
          {renderTimePickerPortal()}
        </div>
      )}

      {/* Confirmation modal shown on success */}
      {/* {showConfirmation && <ScheduleCreationSuccessModel sessionType="Prime" isEdit={true} />} */}
    </div>
  );
};

export default EditPrimeSchedule;
